'use client';

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import type { LocalParticipant, LocalTrackPublication, Room } from 'livekit-client';
import type { ScreenShareCaptureOptions, TrackPublishOptions } from 'livekit-client';
import { ECONOMY_MODE_CHANGED_EVENT, readEconomyModeEnabled } from './economyMode';
import {
  getEffectiveScreenShareQualityProfile,
  getScreenShareCaptureOptions,
  getScreenSharePublishDefaults,
  readScreenShareQualityProfile,
  SCREEN_SHARE_QUALITY_CHANGED_EVENT,
} from './screen-share-profile';

type SetScreenShareEnabled = LocalParticipant['setScreenShareEnabled'];

/**
 * Injeta captura ≤1080p (Normal) ou high opt-in + publish 1 camada em todo
 * setScreenShareEnabled — inclusive o ControlBar do LiveKit, que não aceita
 * captureOptions customizados via props.
 */
export function installScreenShareDefaults(room: Room): () => void {
  const lp = room.localParticipant;
  const original = lp.setScreenShareEnabled as SetScreenShareEnabled;
  const callOriginal = original.bind(lp) as SetScreenShareEnabled;

  const wrapped: SetScreenShareEnabled = async (
    enabled: boolean,
    options?: ScreenShareCaptureOptions,
    publishOptions?: TrackPublishOptions,
  ): Promise<LocalTrackPublication | undefined> => {
    if (!enabled) {
      return callOriginal(enabled, options, publishOptions);
    }

    const economy = readEconomyModeEnabled();
    const profile = getEffectiveScreenShareQualityProfile(
      readScreenShareQualityProfile(),
      economy,
    );
    const mode = economy ? 'economy' : 'default';
    const captureDefaults = getScreenShareCaptureOptions(profile, mode);
    const publishDefaults = getScreenSharePublishDefaults(profile, mode);

    const mergedCapture: ScreenShareCaptureOptions = {
      ...captureDefaults,
      ...options,
      // Prefer our resolution unless caller explicitly overrides with a finite resolution.
      resolution: options?.resolution ?? captureDefaults.resolution,
    };

    const mergedPublish: TrackPublishOptions = {
      ...publishDefaults,
      ...publishOptions,
      screenShareEncoding:
        publishOptions?.screenShareEncoding ?? publishDefaults.screenShareEncoding,
      screenShareSimulcastLayers:
        publishOptions?.screenShareSimulcastLayers ??
        publishDefaults.screenShareSimulcastLayers,
    };

    return callOriginal(enabled, mergedCapture, mergedPublish);
  };

  lp.setScreenShareEnabled = wrapped;

  return () => {
    lp.setScreenShareEnabled = original;
  };
}

/** Ponte React: reinstala o wrap quando preferências mudam. */
export function ScreenShareDefaultsBridge() {
  const room = useRoomContext();

  React.useEffect(() => {
    if (!room) return;
    let uninstall = installScreenShareDefaults(room);

    const reinstall = () => {
      uninstall();
      uninstall = installScreenShareDefaults(room);
    };

    window.addEventListener(SCREEN_SHARE_QUALITY_CHANGED_EVENT, reinstall);
    window.addEventListener(ECONOMY_MODE_CHANGED_EVENT, reinstall);

    return () => {
      uninstall();
      window.removeEventListener(SCREEN_SHARE_QUALITY_CHANGED_EVENT, reinstall);
      window.removeEventListener(ECONOMY_MODE_CHANGED_EVENT, reinstall);
    };
  }, [room]);

  return null;
}
